import { NodeSurface } from '@/components/nodes/shared/node-surface';
import type { CSSProperties, ReactNode } from 'react';

type ProcessingSurfaceProps = {
  aspectRatio?: string | number;
  bgStyle?: CSSProperties;
  insetClass?: string;
  radiusClass?: string;
  className?: string;
  icon?: ReactNode;
  label?: ReactNode;
  footer?: ReactNode;
  progress: number; // 0-100
  bottomRight?: ReactNode;
};

export function ProcessingSurface({
  aspectRatio,
  bgStyle,
  insetClass,
  radiusClass = 'rounded-lg',
  className,
  icon,
  label,
  footer,
  progress,
  bottomRight,
}: ProcessingSurfaceProps) {
  const clamped = Math.max(0, Math.min(100, progress));

  return (
    <NodeSurface
      aspectRatio={aspectRatio}
      bgStyle={bgStyle}
      radiusClass={radiusClass}
      insetClass={insetClass}
      showDashed={false}
      className={className}
    >
      <div
        className="relative h-full w-full overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Number.isFinite(clamped) ? clamped : 0}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6">
          {icon ? (
            <div className="text-muted-foreground/70 [&_svg]:size-16">{icon}</div>
          ) : null}
          {label}
          <div className="h-2.5 w-4/5 max-w-[320px] overflow-hidden rounded-full bg-foreground/10">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
              style={{ width: `${clamped}%` }}
            />
          </div>
          {footer ? <div className="text-center">{footer}</div> : null}
        </div>
        {bottomRight ? (
          <div className="absolute bottom-1 right-2 text-[10px] leading-none text-muted-foreground/90">
            {bottomRight}
          </div>
        ) : null}
      </div>
    </NodeSurface>
  );
}
